import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {AppThunk, RootState} from "../../app/store";
import {RecordModel,
        RecordFactory,
        LayoutCombinedRelatedListModel} from "../../models/models";
import Config from "../../config";

interface CombinedRelatedRecordsState {
  isFetching: boolean;
  relatedRecords: {[key: string]: RecordModel[]};
  error: string | null;
}

const initialState: CombinedRelatedRecordsState = {
  isFetching: false,
  relatedRecords: {},
  error: null
};

export const combinedRelatedRecordsSlice = createSlice({
  name: "combinedRelatedRecords",
  initialState,
  reducers: {
    storeFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    storeRelatedRecords: (state, action: PayloadAction<{layoutRelatedListUuid: string, relatedRecords: RecordModel[]}>) => {
      const layoutRelatedListUuid = action.payload.layoutRelatedListUuid;
      state.relatedRecords[layoutRelatedListUuid] = action.payload.relatedRecords.map((record) => record);
    },
    storeError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    purgeLayoutRelatedList: (state, action: PayloadAction<string>) => {
      //remove only the current key (uuid of current layout related list);
      const layoutRelatedListUuid = action.payload;
      delete state.relatedRecords[layoutRelatedListUuid];
      state.isFetching = false;
      state.error = null;
    },
    purgeStore: (state) => {
      state.relatedRecords = {};
      state.isFetching = false;
      state.error = null;
    },
  },
});



export const { storeRelatedRecords, storeFetching, storeError, purgeLayoutRelatedList, purgeStore } = combinedRelatedRecordsSlice.actions;


export const retrieveRelatedRecords =
  (objectApiName: string | null,
   recordUuid: string | null,
   layoutRelatedList: LayoutCombinedRelatedListModel,
   accessToken: string,
   refetch: boolean,
   handleInvalidToken: () => void): AppThunk =>
  async (dispatch) => {

    let url = `${Config.getInstance().getCoordinationServiceURL()}/api/r/${objectApiName}/${recordUuid}/combined-related`;

    const headers = new Headers();
    const requestBody = {"layout_related_list_uuid": layoutRelatedList.Uuid_sys};

    headers.append("Authorization", `Bearer ${accessToken}`);
    headers.append("Accept", "application/json");
    headers.append("Content-Type", "application/json");


    try {

      dispatch(purgeLayoutRelatedList(layoutRelatedList.Uuid_sys));
      dispatch(storeFetching(true));

      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(requestBody)
      })

      if(response.status===401){
        handleInvalidToken();
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        if(!errorData.message){
          dispatch(storeError("Error when fetching related records"));
        } else{
          dispatch(storeError(errorData.message));
        }
        dispatch(storeFetching(false));
        return;
      } else {
        dispatch(storeError(null));
      }

      const data = await response.json();

      if(!data.records || !Array.isArray(data.records)){
          dispatch(storeError("Error when fetching records"));
          dispatch(storeFetching(false));
          return;
      }

      dispatch(
        storeRelatedRecords({layoutRelatedListUuid: layoutRelatedList.Uuid_sys,
                             relatedRecords: data.records.map((record: any) => new RecordFactory().fromJSON(record))}
                           )
      )

      dispatch(storeFetching(false));

    } catch (error) {
      const errorMessage = "Unhandled error occurred";
      dispatch(storeError(errorMessage));
      dispatch(storeFetching(false));
      console.error(error);
    }
  };

export const selectIsFetching = (state: RootState) =>
  state.combinedRelatedRecordsStore.isFetching;

export const selectError = (state: RootState) =>
  state.combinedRelatedRecordsStore.error;

export const selectRelatedRecords = (state: RootState) =>{
    return state.combinedRelatedRecordsStore.relatedRecords;
}

export default combinedRelatedRecordsSlice.reducer;
